import { CaseListing } from "@/components/CaseListing/CaseListing";
import { ScanDropIndicator } from "@/components/CaseListing/ScanDropIndicator";
import { ROW_HEIGHT } from "@/components/CaseListing/utils";
import { CaseUploadButton, NewEmptyCaseUploadButton } from "@/components/CaseUploadButton/CaseUploadButton";
import { AddPartsSvg } from "@/components/Icon/Icon";
import { addEmptyCase, CaseState, clearSelectedScans, selectAreUploadsSorted } from "@/store/slices/casesSlice";
import { RootState, store } from "@/store/store";
import { Box, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Theme, Typography } from "@mui/material";
import { createSelector } from "@reduxjs/toolkit";
import { FC, forwardRef, useRef } from "react";
import { useDragLayer } from "react-dnd";
import { useTranslation } from "react-i18next";
import { shallowEqual, useSelector } from "react-redux";

const Header = () => {
  const { t } = useTranslation("cases");

  return (
    <TableHead
      sx={{
        height: "52px",
        "& tr th": {
          backgroundColor: (theme) => `${theme.surface.lowest} !important`
        }
      }}
    >
      <TableRow>
        <TableCell padding="checkbox"></TableCell>
        <TableCell>
          <Typography fontWeight={500} sx={{ width: "100%", paddingX: "4px" }}>
            {t("title.name")}
          </Typography>
        </TableCell>
        <TableCell></TableCell>
        <TableCell sx={{ width: "130px" }}>
          <Typography fontWeight={500} sx={{ width: "100%", paddingX: "4px" }}>
            {t("title.scanType")}
          </Typography>
        </TableCell>
        <TableCell padding="checkbox" align="right"></TableCell>
        <TableCell padding="checkbox" align="right"></TableCell>
        <TableCell padding="checkbox" align="right"></TableCell>
        <TableCell padding="checkbox" align="right"></TableCell>
        <TableCell padding="checkbox" align="right"></TableCell>
      </TableRow>
    </TableHead>
  );
};

interface FooterProps {
  handleOnFilesAdded: (files: File[] | DataTransferItem[]) => Promise<void>;
  onNewEmptyCase: () => void;
}

const Footer: FC<FooterProps> = ({ handleOnFilesAdded, onNewEmptyCase }) => {
  const { t } = useTranslation("cases");
  const handleAddCases = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.target.files;
    const filesArray = files !== null ? Array.from(files) : [];
    event.target.value = "";

    // Renaming files to include the whole relative path (if applicable)
    const modifiedFilesArray = filesArray.map((file: File) =>
      file.webkitRelativePath ? new File([file], file.webkitRelativePath, { type: file.type }) : file
    );
    handleOnFilesAdded(modifiedFilesArray);
  };
  return (
    <Box
      sx={{
        overflowX: "auto",
        height: `${ROW_HEIGHT}px`,
        paddingX: "8px",
        borderBottomLeftRadius: "8px",
        borderBottomRightRadius: "8px",
        borderLeft: (theme: Theme) => `2px solid ${theme.outline.main}`,
        borderRight: (theme: Theme) => `2px solid ${theme.outline.main}`,
        borderBottom: (theme: Theme) => `2px solid ${theme.outline.main}`
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: "8px",
          justifyContent: "flex-end",
          alignItems: "center",
          minWidth: "fit-content",
          height: "100%"
        }}
      >
        <NewEmptyCaseUploadButton
          onClick={(e) => {
            e.stopPropagation();
            store.dispatch(addEmptyCase({ name: t("title.newCase"), onNewEmptyCaseCreated: onNewEmptyCase }));
          }}
          variant="text"
          textOnly
        />
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end"
          }}
        >
          <CaseUploadButton textOnly={true} variant={"text"} buttonFunction={handleAddCases} acceptDirectory={false} />
          <CaseUploadButton textOnly={true} variant={"text"} buttonFunction={handleAddCases} acceptDirectory={true} />
        </Box>
      </Box>
    </Box>
  );
};

interface EmptyModelDropzoneProps {
  handleOnFilesAdded: (files: File[] | DataTransferItem[]) => Promise<void>;
}

const EmptyModelDropzone: FC<EmptyModelDropzoneProps> = ({ handleOnFilesAdded }) => {
  const { t } = useTranslation("cases");

  const handleAddCases = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    event.preventDefault();
    event.stopPropagation();

    const files = event.target.files;
    const filesArray = files !== null ? Array.from(files) : [];
    event.target.value = "";

    // Renaming files to include the whole relative path (if applicable)
    const modifiedFilesArray = filesArray.map((file: File) =>
      file.webkitRelativePath ? new File([file], file.webkitRelativePath, { type: file.type }) : file
    );
    handleOnFilesAdded(modifiedFilesArray);
  };

  return (
    <Box
      sx={{
        height: "100%",
        borderRadius: "8px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
        padding: "32px",
        border: "none"
      }}
    >
      <AddPartsSvg />
      <Typography>{t("uploadModal.text.dragAndDrop")}</Typography>
      <Typography>{t("uploadModal.text.or")}</Typography>
      <Box sx={{ display: "flex", flexDirection: "row", gap: "16px" }}>
        <CaseUploadButton secondary={true} buttonFunction={handleAddCases} acceptDirectory={true} />
        <CaseUploadButton secondary={true} buttonFunction={handleAddCases} acceptDirectory={false} />
      </Box>
    </Box>
  );
};

const sortCaseItems = (a: number, b: number) => {
  const getSortKey = (caseData: CaseState | undefined) => {
    if (!caseData) {
      return { group: 3, severity: 0 };
    }
    if (caseData.errors && caseData.errors.length > 0) {
      return { group: 0, severity: Math.max(...caseData.errors.map((e) => e.severity)) };
    }
    if (caseData.warnings && caseData.warnings.length > 0) {
      return { group: 1, severity: Math.max(...caseData.warnings.map((w) => w.severity)) };
    }
    return { group: 2, severity: 0 };
  };

  const caseMapping = store.getState().cases.cases;

  // Retrieve and sort the cases
  const keyA = getSortKey(caseMapping[a]);
  const keyB = getSortKey(caseMapping[b]);

  if (keyA.group !== keyB.group) {
    return keyA.group - keyB.group;
  }

  if (keyA.group < 2) {
    return keyB.severity - keyA.severity;
  }

  return -1;
};

const selectCaseUploadKeys = createSelector(
  [(state: RootState) => state.cases.cases],
  (cases) =>
    Object.keys(cases)
      .map((id) => Number(id))
      .filter((id) => cases[id]?.isProcessed),
  {
    memoizeOptions: {
      resultEqualityCheck: shallowEqual
    }
  }
);

const DragOverlay = () => {
  const { isDragging, currentOffset } = useDragLayer((monitor) => ({
    currentOffset: monitor.getClientOffset(),
    isDragging: monitor.isDragging() && monitor.getItemType() === "scan"
  }));

  if (!isDragging || !currentOffset) {
    return;
  }

  return (
    <Box sx={{ position: "fixed", pointerEvents: "none", zIndex: 100, left: 0, top: 0, width: "100%", height: "100%" }}>
      <Box sx={{ transform: `translate(${currentOffset.x}px, ${currentOffset.y}px)`, position: "relative" }}>
        <ScanDropIndicator />
      </Box>
    </Box>
  );
};

interface ScansSectionProps {
  handleOnFilesAdded: (files: File[] | DataTransferItem[]) => Promise<void>;
}

const UploadTable = forwardRef<HTMLDivElement>(function UploadTable(_, ref) {
  const isSorted = useSelector((state: RootState) => selectAreUploadsSorted(state));
  const caseUploads = useSelector((state: RootState) => selectCaseUploadKeys(state));
  return (
    <TableContainer
      ref={ref}
      sx={{
        height: "100%",
        borderTopLeftRadius: "8px",
        borderTopRightRadius: "8px",
        border: (theme: Theme) => `2px solid ${theme.outline.main}`
      }}
      onClick={() => store.dispatch(clearSelectedScans())}
    >
      <Table
        sx={{
          width: "100%",
          borderCollapse: "collapse"
        }}
        padding="none"
        stickyHeader
      >
        <Header />

        {/* Scans List */}
        <TableBody
          sx={{
            width: "100%",
            overflowY: "auto"
          }}
        >
          <>
            {isSorted
              ? caseUploads
                  .slice() // clone array to avoid mutating state
                  .sort((a, b) => sortCaseItems(a, b))
                  .map((id) => {
                    const dentalCase = store.getState().cases.cases[Number(id)];
                    return <CaseListing key={`${dentalCase.uploadId}`} uploadId={dentalCase.uploadId} />;
                  })
              : caseUploads.map((id) => {
                  const dentalCase = store.getState().cases.cases[Number(id)];
                  return <CaseListing key={`${dentalCase.uploadId}`} uploadId={dentalCase.uploadId} />;
                })}
          </>
        </TableBody>
        <TableFooter>
          <TableRow></TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
});

export const ScansSection: FC<ScansSectionProps> = ({ handleOnFilesAdded }) => {
  const overlayParent = useRef<HTMLDivElement>(null);
  const isEmpty = useSelector((state: RootState) => selectCaseUploadKeys(state).length === 0);
  const tableRef = useRef<HTMLTableElement>(null);
  const scrollToBottom = () => {
    if (tableRef.current) {
      tableRef.current.scrollTo({ top: tableRef.current.scrollHeight, behavior: "smooth" });
    }
  };

  // Check if there are no model files
  if (isEmpty) {
    return (
      <Box sx={{ height: "100%" }}>
        <EmptyModelDropzone handleOnFilesAdded={handleOnFilesAdded} />
      </Box>
    );
  }

  return (
    <>
      <Box ref={overlayParent} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        <UploadTable ref={tableRef} />
        <Footer onNewEmptyCase={scrollToBottom} handleOnFilesAdded={handleOnFilesAdded} />
      </Box>
      <DragOverlay />
    </>
  );
};
